import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { CaseRealities } from "../../shared/PortfolioTypes";
import { TwoRealitiesOutcome } from "../../shared/settlementRealities";
import { Comparison } from "../general/DataAtoms.tsx";
import {
  signedTone,
  TABULAR_NUMERALS,
  toneColors,
} from "../general/dataAtoms/shared.ts";
import { EmptyPanelNote } from "./EmptyPanelNote.tsx";
import { SectionHeading } from "./PortfolioSection.tsx";
import { SettlementPanel } from "./SettlementPanel.tsx";
import { WithoutFeeStructure } from "./WithoutFeeStructure.tsx";

/**
 * One settled-vs-litigated pair on a shared scale. Every metric shown here is
 * "higher is better" — an expected net result, a worst-case net result (less
 * negative is an improvement), the firm's margin and its fee revenue — so one
 * wording of the difference serves them all.
 */
const Pair = ({
  title,
  settledValue,
  litigatedValue,
  level,
}: {
  title: string;
  settledValue: number;
  litigatedValue: number;
  // a revenue is a level; a net result a gain or a loss
  level?: boolean;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement.comparison",
  });
  const better = settledValue - litigatedValue >= 0;
  return (
    <Comparison
      title={title}
      actual={settledValue}
      actualLabel={t("settled")}
      counterfactual={litigatedValue}
      counterfactualLabel={t("litigated")}
      favourable={better}
      differenceText={(amount) =>
        t(`difference.${better ? "better" : "worse"}`, { amount })
      }
      level={level}
    />
  );
};

/**
 * Two pairs of a card: side by side with a rule between them where the page
 * has the width for it, one under the other where it has not.
 */
const Pairs = ({ children }: { children: [ReactNode, ReactNode] }) => {
  const { theme } = useTheme();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          lg: "repeat(2, minmax(0, 1fr))",
        },
        columnGap: 3.5,
        rowGap: 2.5,
        mt: 2,
      }}
    >
      {children[0]}
      <Box
        sx={{
          borderLeft: { lg: `1px solid ${theme.palette.divider}` },
          pl: { lg: 3.5 },
        }}
      >
        {children[1]}
      </Box>
    </Box>
  );
};

/**
 * "Settling vs. not settling" — the two realities side by side, for the
 * clients and for the firm. `settled` here is the portfolio as it actually
 * resolved; the `litigated` counterpart is the same cases with no settlements
 * at all. Both sides come in as `twoWorlds`, the page's single aggregate.
 */
export const SettlementRealities = ({
  realities,
  twoWorlds,
}: {
  realities: CaseRealities[];
  twoWorlds: TwoRealitiesOutcome;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement",
  });

  const { settled, litigated } = twoWorlds;

  const heading = (
    <SectionHeading id="comparison" rule>
      {t("comparison.title")}
    </SectionHeading>
  );

  // the section and its first panel stay when there is nothing to compare, so
  // the nav entry still lands somewhere and the reader learns why it is empty
  if (realities.length === 0)
    return (
      <>
        {heading}
        <SettlementPanel
          label={t("comparison.clientTitle")}
          description={t("comparison.clientDescription")}
        >
          <EmptyPanelNote>{t("noBaseline")}</EmptyPanelNote>
        </SettlementPanel>
      </>
    );

  const firmDelta = settled.firmNet - litigated.firmNet;

  return (
    <>
      {heading}
      <SettlementPanel
        label={t("comparison.clientTitle")}
        // said once, for the pairs of both cards
        description={`${t("comparison.clientDescription")} ${t(
          "comparison.ownScale",
        )}`}
      >
        <Pairs>
          <Pair
            title={t("comparison.clientNet")}
            settledValue={settled.clientNet}
            litigatedValue={litigated.clientNet}
          />
          <Pair
            title={t("comparison.clientExposure")}
            settledValue={settled.clientWorst}
            litigatedValue={litigated.clientWorst}
          />
        </Pairs>
      </SettlementPanel>

      <SettlementPanel
        label={t("comparison.firmTitle")}
        description={t("comparison.firmDescription")}
      >
        <WithoutFeeStructure count={twoWorlds.casesWithoutFeeStructure} />
        <Pairs>
          <Pair
            title={t("comparison.firmMargin")}
            settledValue={settled.firmNet}
            litigatedValue={litigated.firmNet}
          />
          <Pair
            title={t("comparison.firmRevenue")}
            settledValue={settled.firmIncome}
            litigatedValue={litigated.firmIncome}
            level
          />
        </Pairs>
        {/* what settling did to the firm, in one sentence */}
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            pt: 1.75,
            borderTop: 1,
            borderColor: "divider",
            fontWeight: 600,
            color: toneColors(theme)[signedTone(firmDelta)],
            ...TABULAR_NUMERALS,
          }}
        >
          {t(`comparison.firm.${firmDelta >= 0 ? "helped" : "hurt"}`, {
            amount: currency.format(Math.abs(firmDelta)),
          })}
        </Typography>
      </SettlementPanel>
    </>
  );
};
